import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { employeesApi, partsApi, workstationsApi } from '../api/endpoints';
import type { KnownConfiguration, TimelineEvent } from '../api/types';
import { Badge } from '../components/Badge';
import { CancelEventModal } from '../components/CancelEventModal';
import { PartChangeModal } from '../components/PartChangeModal';
import { EventModal } from '../components/EventModal';
import { Notice } from '../components/Notice';
import { Timeline } from '../components/Timeline';
import { WorkstationUpdateModal } from '../components/WorkstationFormModal';
import { FollowReportsButton } from '../desktop/FollowReportsButton';
import { CONFIGURATION_LABELS, diagnosticTypeLabel } from '../domain/diagnostics';
import {
  MANUAL_EVENT_TYPES,
  eventTypeLabel,
  type ManualEventType,
} from '../domain/events';
import {
  PART_OPERATIONS,
  PART_OPERATION_LABELS,
  partCategoryLabel,
  type PartOperation,
} from '../domain/parts';
import { canWrite } from '../domain/roles';
import {
  WORKSTATION_TYPE_LABELS,
  workstationStateLabel,
  workstationStateTone,
} from '../domain/workstations';
import { useAsync } from '../hooks/useAsync';
import { formatDateOnly, formatDateTime, fullName } from '../lib/format';
import { useLiveEvents } from '../realtime/RealtimeContext';
import { useCurrentUser } from '../session/SessionContext';

function ConfigurationPanel({ configuration }: { configuration: KnownConfiguration | null }) {
  if (configuration === null) {
    return (
      <p className="doux">
        Aucun diagnostic importé pour ce poste : sa configuration n'est pas connue.
      </p>
    );
  }

  const entries = Object.entries(configuration.values).filter(
    ([, value]) => value !== null && value !== undefined && value !== '',
  );

  return (
    <div className="pile pile--serree">
      <div className="faible">
        D'après le diagnostic {diagnosticTypeLabel(configuration.diagnosticType)} du{' '}
        {formatDateTime(configuration.performedAt)}.
      </div>

      {configuration.extractionStatus !== 'ok' && (
        <Notice tone="alerte" title="Extraction sans résultat">
          L'extraction automatique n'a rien donné pour ce diagnostic
          {configuration.extractionError === null
            ? '.'
            : ` : ${configuration.extractionError}.`}{' '}
          Seules les valeurs saisies à la main figurent ci-dessous.
        </Notice>
      )}

      {entries.length === 0 ? (
        <p className="doux">Aucune valeur de configuration n'a été confirmée.</p>
      ) : (
        <dl className="definitions">
          {entries.map(([key, value]) => (
            <div key={key} style={{ display: 'contents' }}>
              <dt>{CONFIGURATION_LABELS[key] ?? key}</dt>
              <dd>
                {Array.isArray(value) ? (
                  <ul className="liste-nue">
                    {value.map((item, index) => (
                      <li key={index}>{String(item)}</li>
                    ))}
                  </ul>
                ) : (
                  String(value)
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

export function WorkstationPage() {
  const { id = '' } = useParams();
  const user = useCurrentUser();
  const writable = canWrite(user.role);

  const [editing, setEditing] = useState(false);
  const [eventType, setEventType] = useState<ManualEventType | null>(null);
  const [cancelling, setCancelling] = useState<TimelineEvent | null>(null);
  const [partOperation, setPartOperation] = useState<PartOperation | null>(null);

  const workstation = useAsync((signal) => workstationsApi.get(id, signal), [id]);
  const timeline = useAsync((signal) => workstationsApi.events(id, signal), [id]);
  const employees = useAsync((signal) => employeesApi.list({}, signal), []);
  const parts = useAsync((signal) => workstationsApi.parts(id, undefined, signal), [id]);
  const references = useAsync((signal) => partsApi.listReferences({}, signal), []);

  const refreshAll = (): void => {
    workstation.reload();
    timeline.reload();
    parts.reload();
    references.reload();
  };

  /**
   * Un événement poussé concernant ce poste.
   *
   * La frise reçoit l'événement lui-même, inséré à sa place : c'est tout
   * l'intérêt de diffuser l'objet plutôt qu'un signal, et cela évite de
   * redemander une page entière pour une ligne.
   *
   * Ce qui DÉRIVE du journal — l'état, le propriétaire, les pièces montées,
   * la configuration connue — est relu au serveur. Le recalculer ici serait
   * une troisième implémentation de règles qui en ont déjà deux, et la
   * troisième divergerait comme les autres.
   */
  useLiveEvents((event) => {
    if (event.workstationId !== id) return;

    timeline.patch((current) => {
      if (current.events.some((connu) => connu.id === event.id)) return current;

      const events = [...current.events];
      // une annulation ne s'ajoute pas seulement : elle marque sa cible, que
      // la frise affiche barrée
      if (event.cancels !== null) {
        const cible = events.findIndex((e) => e.id === event.cancels!.eventId);
        if (cible !== -1) {
          events[cible] = {
            ...events[cible]!,
            cancelled: true,
            cancellation: {
              eventId: event.id,
              occurredAt: event.occurredAt,
              reason: event.reason,
              author: event.author.displayName,
            },
          };
        }
      }

      events.push(event);
      // même ordre que le serveur : du plus récent au plus ancien
      events.sort((a, b) => {
        if (a.occurredAt !== b.occurredAt) return a.occurredAt < b.occurredAt ? 1 : -1;
        if (a.recordedAt !== b.recordedAt) return a.recordedAt < b.recordedAt ? 1 : -1;
        return 0;
      });
      return { events };
    });

    workstation.reload();
    if (event.type === 'changementPiece') {
      parts.reload();
      references.reload();
    }
  });

  if (workstation.error !== null) {
    return (
      <Notice tone="erreur" title="Poste introuvable">
        {workstation.error}
      </Notice>
    );
  }

  const detail = workstation.data?.workstation ?? null;
  if (detail === null) {
    return <div className="chargement">Chargement du poste…</div>;
  }

  return (
    <div className="pile">
      <div className="entete-page">
        <div>
          <h1>{detail.name}</h1>
          <p className="entete-page__sous-titre">
            {detail.brand} {detail.model} · {WORKSTATION_TYPE_LABELS[detail.type]}
          </p>
        </div>
        <div className="ligne">
          <Badge tone={workstationStateTone(detail.state)}>
            {workstationStateLabel(detail.state)}
          </Badge>
          {writable && (
            <button
              type="button"
              className="bouton"
              onClick={() => setEditing(true)}
            >
              Modifier la fiche
            </button>
          )}
        </div>
      </div>

      {/* sans effet dans le navigateur, où le composant ne rend rien */}
      <FollowReportsButton workstationId={detail.id} workstationName={detail.name} />

      {/*
        L'import est ici, parmi les autres actions d'enregistrement, et non
        dans la barre de navigation : il suppose un poste, et c'est celui-ci.
      */}
      {writable && (
        <div className="outils">
          {MANUAL_EVENT_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              className="bouton"
              onClick={() => setEventType(type)}
            >
              {eventTypeLabel(type)}
            </button>
          ))}
          <Link className="bouton bouton--primaire" to={`/postes/${detail.id}/import`}>
            Importer un diagnostic
          </Link>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gap: 'var(--espace-5)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        }}
      >
        <section className="panneau">
          <div className="panneau__entete">
            <span className="panneau__titre">Identité</span>
          </div>
          <div className="panneau__corps">
            <dl className="definitions">
              <dt>Nom interne</dt>
              <dd>{detail.name}</dd>
              <dt>Marque et modèle</dt>
              <dd>
                {detail.brand} {detail.model}
              </dd>
              <dt>Numéro de série</dt>
              <dd>{detail.serialNumber ?? <span className="faible">Non renseigné</span>}</dd>
              <dt>Type</dt>
              <dd>{WORKSTATION_TYPE_LABELS[detail.type]}</dd>
              <dt>État courant</dt>
              <dd>
                {workstationStateLabel(detail.state)}
                <div className="faible">
                  Calculé depuis le journal : il se corrige par un événement de
                  changement d'état.
                </div>
              </dd>
              <dt>Mise en service</dt>
              <dd>{formatDateOnly(detail.commissionedOn)}</dd>
              <dt>Propriétaire courant</dt>
              <dd>
                {detail.currentOwner === null ? (
                  <span className="faible">Aucun</span>
                ) : (
                  `${fullName(detail.currentOwner)} — ${detail.currentOwner.department}`
                )}
              </dd>
              <dt>Dernière action</dt>
              <dd>{formatDateTime(detail.lastActionAt)}</dd>
              {detail.note !== '' && (
                <>
                  <dt>Note</dt>
                  <dd style={{ whiteSpace: 'pre-wrap' }}>{detail.note}</dd>
                </>
              )}
            </dl>
          </div>
        </section>

        <section className="panneau">
          <div className="panneau__entete">
            <span className="panneau__titre">Configuration connue</span>
          </div>
          <div className="panneau__corps">
            <ConfigurationPanel configuration={detail.configuration} />
          </div>
        </section>
      </div>

      <section className="panneau">
        <div className="panneau__entete">
          <span className="panneau__titre">Pièces montées</span>
          <span className="faible">Calculées depuis le journal</span>
          {writable && (
            <div className="ligne pousse">
              {PART_OPERATIONS.map((operation) => (
                <button
                  key={operation}
                  type="button"
                  className="bouton bouton--petit"
                  disabled={
                    operation !== 'montage' && (parts.data?.parts.length ?? 0) === 0
                  }
                  onClick={() => setPartOperation(operation)}
                >
                  {PART_OPERATION_LABELS[operation]}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="panneau__corps panneau__corps--nu">
          {parts.error !== null && (
            <div style={{ padding: 'var(--espace-5)' }}>
              <Notice tone="erreur" title="Pièces indisponibles">
                {parts.error}
              </Notice>
            </div>
          )}
          {parts.data !== null && parts.data.parts.length === 0 && (
            <div className="tableau__vide">Aucune pièce montée sur ce poste.</div>
          )}
          {parts.data !== null && parts.data.parts.length > 0 && (
            <div className="tableau-cadre">
              <table className="tableau">
                <thead>
                  <tr>
                    <th>Catégorie</th>
                    <th>Désignation</th>
                    <th>Numéro de série</th>
                    <th>Quantité</th>
                  </tr>
                </thead>
                <tbody>
                  {parts.data.parts.map((part) => (
                    <tr key={part.itemId ?? part.referenceId}>
                      <td>{partCategoryLabel(part.category)}</td>
                      <td>
                        {part.designation}
                        <div className="faible">
                          {part.brand} {part.model}
                        </div>
                      </td>
                      <td className="mono">
                        {part.serialNumber ?? <span className="faible">—</span>}
                      </td>
                      <td className="tableau__nombre">{part.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="panneau">
        <div className="panneau__entete">
          <span className="panneau__titre">Frise chronologique</span>
          <span className="faible pousse">Du plus récent au plus ancien</span>
        </div>
        <div className="panneau__corps panneau__corps--nu">
          {timeline.error !== null && (
            <div style={{ padding: 'var(--espace-5)' }}>
              <Notice tone="erreur" title="Journal indisponible">
                {timeline.error}
              </Notice>
            </div>
          )}
          {timeline.loading && timeline.data === null && (
            <div className="chargement">Chargement du journal…</div>
          )}
          {timeline.data !== null && (
            <Timeline
              events={timeline.data.events}
              employees={employees.data?.employees ?? []}
              canCancel={writable}
              onCancel={setCancelling}
            />
          )}
        </div>
      </section>

      {editing && (
        <WorkstationUpdateModal
          workstation={detail}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            refreshAll();
          }}
        />
      )}

      {eventType !== null && (
        <EventModal
          type={eventType}
          workstationId={detail.id}
          currentState={detail.state}
          employees={employees.data?.employees ?? []}
          events={timeline.data?.events ?? []}
          onClose={() => setEventType(null)}
          onSaved={() => {
            setEventType(null);
            refreshAll();
          }}
        />
      )}

      {partOperation !== null && (
        <PartChangeModal
          operation={partOperation}
          workstationId={detail.id}
          references={references.data?.references ?? []}
          mounted={parts.data?.parts ?? []}
          onClose={() => setPartOperation(null)}
          onSaved={() => {
            setPartOperation(null);
            refreshAll();
          }}
        />
      )}

      {cancelling !== null && (
        <CancelEventModal
          target={cancelling}
          onClose={() => setCancelling(null)}
          onSaved={() => {
            setCancelling(null);
            refreshAll();
          }}
        />
      )}
    </div>
  );
}
